import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BOSS_DISRUPT_DURATION_MS,
  evaluateBossRun,
  judgeBossTap,
  type BossInputEvent,
  type BossSnapshot,
} from '@shared/boss';
import { PALETTE } from '@shared/gameConfig';
import type { ColorType } from '@shared/types';
import { SkillIcon } from '@/art';
import { audio } from '@/audio/AudioService';
import { SkillButton as MatchSkillButton } from '@/screens/match/SkillButton';
import { resolveAssetUrl } from '@/shop/assetUrl';
import { useBossStore } from '@/stores/bossStore';
import { useUIStore } from '@/stores/uiStore';
import { useUserStore } from '@/stores/userStore';
import { useT } from '@/i18n/useI18n';
import type { I18nKey } from '@/i18n/translations';
import { bossArenaImage } from '@/boss/bossAssets';
import { localizedBossName } from '@/boss/bossLocalization';

type BattlePhase = 'countdown' | 'playing' | 'submitting' | 'result';
type BossAttackKind = 'slam' | 'shards' | 'beam';
type BossAttackResult = 'hit' | 'blocked' | 'interrupted';
type TimingFeedback = 'hit' | 'early' | 'late' | 'wrong';

interface BossAttackFx {
  id: number;
  kind: BossAttackKind;
  result: BossAttackResult;
}

const ATTACK_LABEL_KEYS: Record<BossAttackKind, I18nKey> = {
  slam: 'boss.attack.slam',
  shards: 'boss.attack.shards',
  beam: 'boss.attack.beam',
};

const TIMING_FEEDBACK_KEYS: Record<TimingFeedback, I18nKey> = {
  hit: 'boss.timing.hit',
  early: 'boss.timing.early',
  late: 'boss.timing.late',
  wrong: 'boss.timing.wrong',
};

const STONE_KING_ACTION_IMAGES: Record<BossAttackKind, string> = {
  slam: '/assets/bosses/stone-king-slam.png',
  shards: '/assets/bosses/stone-king-shards.png',
  beam: '/assets/bosses/stone-king-beam.png',
};

function getBossActionImages(
  imageUrl: string | null,
): Record<BossAttackKind, string> | null {
  if (!imageUrl?.includes('/stone-king.png')) return null;
  return STONE_KING_ACTION_IMAGES;
}

function attackKindFor(index: number, phase: BossSnapshot['phase']): BossAttackKind {
  const rotation: BossAttackKind[] =
    phase === 1
      ? ['slam', 'shards', 'slam']
      : phase === 2
        ? ['shards', 'slam', 'beam']
        : ['beam', 'shards', 'slam'];
  return rotation[(index - 1) % rotation.length]!;
}

const KEY_COLORS: Record<string, ColorType> = {
  a: 'red',
  s: 'blue',
  d: 'yellow',
  f: 'purple',
};

const ARENA_DUST = [
  { x: 9, y: 16, delay: -1.2, duration: 7.4 },
  { x: 18, y: 67, delay: -4.8, duration: 8.1 },
  { x: 29, y: 38, delay: -2.5, duration: 6.8 },
  { x: 42, y: 78, delay: -6.1, duration: 9.2 },
  { x: 53, y: 24, delay: -3.4, duration: 7.7 },
  { x: 64, y: 58, delay: -5.7, duration: 8.6 },
  { x: 73, y: 31, delay: -0.9, duration: 6.9 },
  { x: 82, y: 73, delay: -7.2, duration: 9.5 },
  { x: 91, y: 44, delay: -2.8, duration: 7.2 },
] as const;

export function BossBattleScreen() {
  const t = useT();
  const go = useUIStore((state) => state.go);
  const run = useBossStore((state) => state.activeRun);
  const settlement = useBossStore((state) => state.settlement);
  const finish = useBossStore((state) => state.finish);
  const clearRun = useBossStore((state) => state.clearRun);
  const loadBosses = useBossStore((state) => state.load);
  const setProfile = useUserStore((state) => state.setProfile);
  const characterUrl = useUserStore(
    (state) => state.profile.characterIdleUrl ?? null,
  );
  const eventsRef = useRef<BossInputEvent[]>([]);
  const playStartedAtRef = useRef(0);
  const lastTapAtRef = useRef(-1_000);
  const submittingRef = useRef(false);
  const observedHitsRef = useRef(0);
  const observedBlocksRef = useRef(0);
  const attackFxTimerRef = useRef<number | null>(null);
  const timingFeedbackTimerRef = useRef<number | null>(null);
  const [phase, setPhase] = useState<BattlePhase>('countdown');
  const [countdown, setCountdown] = useState(3);
  const [pressed, setPressed] = useState<ColorType | null>(null);
  const [flash, setFlash] = useState<'good' | 'miss' | 'skill' | null>(null);
  const [timingFeedback, setTimingFeedback] =
    useState<TimingFeedback | null>(null);
  const [attackFx, setAttackFx] = useState<BossAttackFx | null>(null);
  const [characterImageFailed, setCharacterImageFailed] = useState(false);
  const initialSnapshot = useMemo(
    () =>
      run
        ? evaluateBossRun(run.config, run.seed, [], 0)
        : null,
    [run],
  );
  const [snapshot, setSnapshot] = useState<BossSnapshot | null>(
    initialSnapshot,
  );
  const actionImages = useMemo(
    () => getBossActionImages(run?.config.imageUrl ?? null),
    [run?.config.imageUrl],
  );
  const arenaImage = useMemo(
    () => bossArenaImage(run?.config.imageUrl ?? null, run?.config.slug),
    [run?.config.imageUrl, run?.config.slug],
  );

  useEffect(() => {
    if (!actionImages) return;
    Object.values(actionImages).forEach((src) => {
      const image = new Image();
      image.decoding = 'async';
      image.src = src;
    });
  }, [actionImages]);

  useEffect(() => {
    setCharacterImageFailed(false);
  }, [characterUrl]);

  useEffect(() => {
    if (!run) {
      go('bossSelect');
      return;
    }
    eventsRef.current = [];
    submittingRef.current = false;
    observedHitsRef.current = 0;
    observedBlocksRef.current = 0;
    setAttackFx(null);
    setTimingFeedback(null);
    if (attackFxTimerRef.current != null) {
      window.clearTimeout(attackFxTimerRef.current);
      attackFxTimerRef.current = null;
    }
    if (timingFeedbackTimerRef.current != null) {
      window.clearTimeout(timingFeedbackTimerRef.current);
      timingFeedbackTimerRef.current = null;
    }
    setSnapshot(evaluateBossRun(run.config, run.seed, [], 0));
    setPhase('countdown');
    setCountdown(3);
    let value = 3;
    const timer = window.setInterval(() => {
      value -= 1;
      setCountdown(value);
      audio.play(value > 0 ? 'match.countdown' : 'match.go');
      if (value <= 0) {
        window.clearInterval(timer);
        playStartedAtRef.current = performance.now();
        setPhase('playing');
      }
    }, 850);
    return () => {
      window.clearInterval(timer);
      if (attackFxTimerRef.current != null) {
        window.clearTimeout(attackFxTimerRef.current);
      }
      if (timingFeedbackTimerRef.current != null) {
        window.clearTimeout(timingFeedbackTimerRef.current);
      }
    };
  }, [go, run]);

  const currentElapsed = useCallback((): number => {
    if (!run || playStartedAtRef.current <= 0) return 0;
    return Math.min(
      run.config.durationMs,
      Math.max(0, Math.floor(performance.now() - playStartedAtRef.current)),
    );
  }, [run]);

  const settle = useCallback(
    async (elapsed: number): Promise<void> => {
      if (!run || submittingRef.current) return;
      submittingRef.current = true;
      setPhase('submitting');
      try {
        const result = await finish(elapsed, eventsRef.current);
        setProfile({
          coins: result.wallet.coins,
          gems: result.wallet.gems,
        });
        void loadBosses();
        audio.play(result.outcome === 'win' ? 'match.victory' : 'match.defeat');
        setPhase('result');
      } catch {
        submittingRef.current = false;
        setPhase('playing');
      }
    },
    [finish, loadBosses, run, setProfile],
  );

  useEffect(() => {
    if (!run || phase !== 'playing') return;
    const timer = window.setInterval(() => {
      const elapsed = currentElapsed();
      const next = evaluateBossRun(
        run.config,
        run.seed,
        eventsRef.current,
        elapsed,
      );
      setSnapshot(next);
      if (next.completed || next.defeated || elapsed >= run.config.durationMs) {
        void settle(elapsed);
      }
    }, 50);
    return () => window.clearInterval(timer);
  }, [currentElapsed, phase, run, settle]);

  useEffect(() => {
    if (!snapshot || phase !== 'playing') return;
    const hitDelta = snapshot.attacksTaken - observedHitsRef.current;
    const blockDelta = snapshot.attacksBlocked - observedBlocksRef.current;
    if (hitDelta <= 0 && blockDelta <= 0) return;

    observedHitsRef.current = snapshot.attacksTaken;
    observedBlocksRef.current = snapshot.attacksBlocked;
    const attackIndex = snapshot.attacksTaken + snapshot.attacksBlocked;
    const result: BossAttackResult =
      blockDelta <= 0 ? 'hit' : snapshot.stunned ? 'interrupted' : 'blocked';
    setAttackFx({
      id: attackIndex,
      kind: attackKindFor(attackIndex, snapshot.phase),
      result,
    });
    audio.play(
      result === 'hit'
        ? 'match.fall'
        : result === 'blocked'
          ? 'match.skill_shield'
          : 'match.skill_disrupt',
    );
    if (navigator.vibrate) {
      navigator.vibrate(
        result === 'hit'
          ? [55, 30, 75]
          : result === 'blocked'
            ? [20, 30, 20]
            : [10, 24, 10],
      );
    }
    if (attackFxTimerRef.current != null) {
      window.clearTimeout(attackFxTimerRef.current);
    }
    attackFxTimerRef.current = window.setTimeout(() => {
      setAttackFx(null);
      attackFxTimerRef.current = null;
    }, 820);
  }, [
    phase,
    snapshot?.attacksBlocked,
    snapshot?.attacksTaken,
    snapshot?.phase,
  ]);

  const pushEvent = useCallback(
    (event: BossInputEvent): void => {
      if (!run || phase !== 'playing') return;
      eventsRef.current.push(event);
      const next = evaluateBossRun(
        run.config,
        run.seed,
        eventsRef.current,
        event.atMs,
      );
      setSnapshot(next);
      if (next.completed || next.defeated) void settle(event.atMs);
    },
    [phase, run, settle],
  );

  const tapColor = useCallback(
    (color: ColorType): void => {
      if (!run || phase !== 'playing') return;
      const atMs = currentElapsed();
      if (atMs - lastTapAtRef.current < 60) return;
      lastTapAtRef.current = atMs;
      const beforeTap = evaluateBossRun(
        run.config,
        run.seed,
        eventsRef.current,
        atMs,
      );
      const feedback: TimingFeedback = judgeBossTap(
        beforeTap.targets,
        color,
      ).result;
      const successful = feedback === 'hit';
      pushEvent({ kind: 'tap', color, atMs });
      setPressed(color);
      setFlash(successful ? 'good' : 'miss');
      setTimingFeedback(feedback);
      window.setTimeout(() => setPressed(null), 110);
      window.setTimeout(() => setFlash(null), 260);
      if (timingFeedbackTimerRef.current != null) {
        window.clearTimeout(timingFeedbackTimerRef.current);
      }
      timingFeedbackTimerRef.current = window.setTimeout(() => {
        setTimingFeedback(null);
        timingFeedbackTimerRef.current = null;
      }, 420);
      audio.play(successful ? 'ui.tap' : 'match.fall');
      if (navigator.vibrate) navigator.vibrate(successful ? 8 : 35);
    },
    [currentElapsed, phase, pushEvent, run],
  );

  const useSkill = useCallback(
    (kind: 'shield' | 'disrupt'): void => {
      if (!snapshot || phase !== 'playing') return;
      const ready =
        kind === 'shield'
          ? snapshot.shieldCooldownMs <= 0
          : snapshot.disruptCooldownMs <= 0;
      if (!ready) return;
      pushEvent({ kind, atMs: currentElapsed() });
      setFlash('skill');
      window.setTimeout(() => setFlash(null), 300);
      audio.play(
        kind === 'shield' ? 'match.skill_shield' : 'match.skill_disrupt',
      );
    },
    [currentElapsed, phase, pushEvent, snapshot],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const color = KEY_COLORS[event.key.toLowerCase()];
      if (color) tapColor(color);
      if (event.key.toLowerCase() === 'q') useSkill('disrupt');
      if (event.key.toLowerCase() === 'e') useSkill('shield');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tapColor, useSkill]);

  if (!run || !snapshot) return null;

  const hpPct = (snapshot.bossHpRemaining / run.config.hp) * 100;
  const gripPct = (snapshot.grip / run.config.gripMax) * 100;
  const staggerPct = (snapshot.stagger / run.config.staggerMax) * 100;
  const timeLeft = Math.max(0, run.config.durationMs - snapshot.elapsedMs);
  const trackStartPct = 6;
  const trackSpanPct = 88;
  const movingTargets = snapshot.targets.map((target) => ({
    ...target,
    leftPct: trackStartPct + target.progress * trackSpanPct,
  }));
  const hitFromRatio = snapshot.perfectFromMs / snapshot.targetWindowMs;
  const hitToRatio = snapshot.perfectToMs / snapshot.targetWindowMs;
  const hitZoneLeftPct = trackStartPct + hitFromRatio * trackSpanPct;
  const hitZoneWidthPct = (hitToRatio - hitFromRatio) * trackSpanPct;
  const attackWarning =
    phase === 'playing' && snapshot.nextAttackInMs > 0
      && snapshot.nextAttackInMs < 850;
  const upcomingAttack = attackKindFor(
    snapshot.attacksTaken + snapshot.attacksBlocked + 1,
    snapshot.phase,
  );
  const activeBossPose = attackFx?.kind ?? (attackWarning ? upcomingAttack : null);
  const resolvedCharacterUrl = resolveAssetUrl(characterUrl);
  const difficultyLabel = t(`boss.difficulty.${run.config.difficulty}`);
  const bossName = localizedBossName(t, run.config);

  return (
    <div
      className={`screen boss-battle-screen phase-${snapshot.phase} ${
        attackWarning ? 'boss-attack-warning' : ''
      } ${flash === 'good' ? 'boss-perfect-impact' : ''} ${
        attackFx ? `boss-cast-${attackFx.kind} boss-attack-${attackFx.result}` : ''
      }`}
      style={{ '--boss-accent': run.config.accentColor } as React.CSSProperties}
    >
      <div className="hex-bg" />
      <header className="boss-hud">
        <button
          className="boss-retreat"
          onClick={() => void settle(currentElapsed())}
          disabled={phase !== 'playing'}
        >
          ×
        </button>
        <div className="boss-hud-center">
          <div className="boss-name-row">
            <span className="tag">
              {t('boss.phase', {
                phase: snapshot.phase,
                difficulty: difficultyLabel,
              })}
            </span>
            <span className="mono">{(timeLeft / 1000).toFixed(1)}s</span>
          </div>
          <Meter
            value={hpPct}
            color="var(--c-red)"
            label={t('boss.hp', {
              name: bossName,
              remaining: snapshot.bossHpRemaining,
              total: run.config.hp,
            })}
          />
          <Meter
            value={staggerPct}
            color="var(--c-yellow)"
            label={
              snapshot.stunned
                ? t('boss.stunned_break')
                : t('boss.break_meter')
            }
          />
        </div>
      </header>

      <main className="boss-arena">
        <div className="boss-combat-readout">
          <span className="display">
            {t('boss.score_combo', {
              score: snapshot.score.toLocaleString(),
              n: snapshot.combo,
            })}
          </span>
          <span className="tag">
            {snapshot.vulnerable
              ? t('boss.vulnerable', {
                  seconds: Math.ceil(BOSS_DISRUPT_DURATION_MS / 1000),
                })
              : snapshot.stunned
                ? t('boss.burst_window')
                : attackWarning
                  ? t(ATTACK_LABEL_KEYS[upcomingAttack])
                  : t('boss.destroy_core')}
          </span>
        </div>

        <div className="boss-visual-wrap">
          <div
            className={`boss-arena-environment ${
              arenaImage ? 'has-image' : ''
            }`}
            style={
              arenaImage
                ? ({
                    '--boss-arena-image': `url("${arenaImage}")`,
                  } as React.CSSProperties)
                : undefined
            }
            aria-hidden="true"
          >
            <div className="boss-arena-environment__backdrop" />
            <div className="boss-arena-environment__haze" />
            <div className="boss-arena-environment__dust">
              {ARENA_DUST.map((particle, index) => (
                <i
                  key={index}
                  style={
                    {
                      '--dust-x': `${particle.x}%`,
                      '--dust-y': `${particle.y}%`,
                      '--dust-delay': `${particle.delay}s`,
                      '--dust-duration': `${particle.duration}s`,
                    } as React.CSSProperties
                  }
                />
              ))}
            </div>
            <div className="boss-arena-environment__ground-glow" />
          </div>
          {run.config.imageUrl ? (
            <BossActionArt
              idleUrl={run.config.imageUrl}
              actionImages={actionImages}
              activePose={activeBossPose}
              stunned={snapshot.stunned}
            />
          ) : (
            <div
              className={`stone-boss ${
                snapshot.stunned ? 'stone-boss--stunned' : ''
              }`}
            >
              <div className="stone-boss-horn stone-boss-horn--left" />
              <div className="stone-boss-horn stone-boss-horn--right" />
              <div className="stone-boss-head">
                <i />
                <i />
              </div>
              <div className="stone-boss-arm stone-boss-arm--left" />
              <div className="stone-boss-arm stone-boss-arm--right" />
              <div className="stone-boss-body">
                {(['red', 'blue', 'yellow', 'purple'] as ColorType[]).map(
                  (color) => (
                    <b
                      key={color}
                      className={
                        snapshot.targetColor === color ? 'is-target' : ''
                      }
                      style={{ background: PALETTE[color] }}
                    />
                  ),
                )}
              </div>
            </div>
          )}
          <div
            className={`boss-target-lane ${flash ? `flash-${flash}` : ''}`}
            aria-label={t('boss.current_color_aria', {
              color: t(`boss.color.${snapshot.targetColor}`),
            })}
          >
            <div className="boss-target-lane__rail" />
            <div
              className="boss-target-lane__hit-zone"
              style={{
                left: `${hitZoneLeftPct}%`,
                width: `${hitZoneWidthPct}%`,
              }}
            >
              <span>{t('boss.timing.zone')}</span>
            </div>
            {movingTargets.map((target) => (
              <i
                key={target.index}
                className="boss-target-lane__moving-target"
                style={
                  {
                    left: `${target.leftPct}%`,
                    '--target-color': PALETTE[target.color],
                  } as React.CSSProperties
                }
              />
            ))}
            {timingFeedback && (
              <strong className={`boss-timing-feedback is-${timingFeedback}`}>
                {t(TIMING_FEEDBACK_KEYS[timingFeedback])}
              </strong>
            )}
          </div>
          <div
            className={`boss-player ${
              attackFx && attackFx.result !== 'interrupted'
                ? `boss-player--${attackFx.result}`
                : ''
            }`}
          >
            {resolvedCharacterUrl && !characterImageFailed ? (
              <img
                src={resolvedCharacterUrl}
                alt=""
                onError={() => setCharacterImageFailed(true)}
              />
            ) : (
              <span>🧗</span>
            )}
            {snapshot.shieldActive && <i className="boss-player-shield" />}
          </div>
          {attackFx && (
            <BossAttackEffect
              key={attackFx.id}
              effect={attackFx}
              damage={run.config.attackDamage}
            />
          )}
        </div>

        <div className="boss-grip">
          <Meter
            value={gripPct}
            color={
              gripPct > 45
                ? 'var(--c-cyan)'
                : gripPct > 22
                  ? 'var(--c-yellow)'
                  : 'var(--c-red)'
            }
            label={t('boss.grip', {
              current: snapshot.grip,
              max: run.config.gripMax,
            })}
          />
        </div>
      </main>

      <footer className="boss-controls match-controls">
        <MatchSkillButton
          icon={<SkillIcon skill="disrupt" size={48} />}
          cd={snapshot.disruptCooldownMs / 1000}
          onFire={() => useSkill('disrupt')}
          color="var(--c-purple)"
          label={t('boss.skill.disrupt')}
        />
        <div className="boss-color-grid">
          {(['red', 'blue', 'yellow', 'purple'] as ColorType[]).map(
            (color) => (
              <button
                key={color}
                aria-label={t('boss.tap_color_aria', {
                  color: t(`boss.color.${color}`),
                })}
                className={`color-disc color-disc--${color}`}
                data-pressed={pressed === color}
                disabled={phase !== 'playing'}
                onPointerDown={(event) => {
                  event.preventDefault();
                  tapColor(color);
                }}
              />
            ),
          )}
        </div>
        <MatchSkillButton
          icon={<SkillIcon skill="shield" size={48} />}
          cd={snapshot.shieldCooldownMs / 1000}
          onFire={() => useSkill('shield')}
          color="var(--c-cyan)"
          label={t('boss.skill.shield')}
        />
      </footer>

      {phase === 'countdown' && (
        <div className="boss-overlay">
          <div className="tag">{t('boss.title')}</div>
          <div className="display boss-countdown">
            {countdown || t('boss.go')}
          </div>
          <p>{t('boss.countdown_hint')}</p>
        </div>
      )}
      {phase === 'submitting' && (
        <div className="boss-overlay">
          <div className="display">{t('boss.verifying')}</div>
        </div>
      )}
      {phase === 'result' && settlement && (
        <div className="boss-overlay boss-result-overlay">
          <div className="tag">
            {settlement.outcome === 'win'
              ? settlement.snapshot.victory
                ? t('boss.defeated')
                : t('boss.run_complete')
              : t('boss.run_failed')}
          </div>
          <div className="display boss-result-title">
            {settlement.outcome === 'win'
              ? settlement.snapshot.victory
                ? t('boss.finish')
                : t('boss.score_recorded')
              : t('boss.grip_empty')}
          </div>
          <div className="boss-result-stats">
            <span>
              {t('boss.stat.score')}{' '}
              <b>
                {(
                  settlement.snapshot.score ?? settlement.snapshot.damage
                ).toLocaleString()}
              </b>
            </span>
            <span>
              {t('boss.stat.perfect')}{' '}
              <b>
                {settlement.snapshot.perfectHits ?? 0}
                {' · '}
                {settlement.snapshot.perfectRate ?? 0}%
              </b>
            </span>
            <span>
              {t('boss.stat.combo')} <b>{settlement.snapshot.bestCombo}</b>
            </span>
          </div>
          <div className="boss-result-reward">
            +{settlement.rewards.coins} 🪙 · +{settlement.rewards.gems} 💎
            {settlement.rewards.frameGranted
              ? ` · ${t('boss.frame_unlocked')}`
              : ''}
            {settlement.rewards.frameRequirement
              && !settlement.rewards.frameRequirement.met
              ? ` · ${t('boss.frame_need_perfect', {
                  rate: settlement.rewards.frameRequirement.requiredPerfectRate,
                })}`
              : ''}
            {settlement.rewards.weeklyBonus?.frameGranted
              ? ` · ${t('boss.legendary_frame')}`
              : ''}
            {(settlement.rewards.weeklyBonus?.coins ?? 0) > 0
              || (settlement.rewards.weeklyBonus?.gems ?? 0) > 0
              ? ` · ${t('boss.weekly_bonus')}`
              : ''}
            {settlement.rewards.dailyCapReached
              ? ` · ${t('boss.daily_cap')}`
              : ''}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              clearRun();
              go('bossSelect');
            }}
          >
            {t('boss.continue')}
          </button>
          <button
            className="btn"
            onClick={() => {
              clearRun();
              go('home');
            }}
          >
            {t('boss.home')}
          </button>
        </div>
      )}
    </div>
  );
}

function BossActionArt({
  idleUrl,
  actionImages,
  activePose,
  stunned,
}: {
  idleUrl: string;
  actionImages: Record<BossAttackKind, string> | null;
  activePose: BossAttackKind | null;
  stunned: boolean;
}) {
  if (!actionImages) {
    return (
      <div
        className={`boss-art-stage ${
          stunned ? 'boss-uploaded-art--stunned' : ''
        }`}
      >
        <img
          className="boss-art-layer boss-art-layer--idle is-active"
          src={idleUrl}
          alt=""
        />
      </div>
    );
  }

  return (
    <div
      className={`boss-art-stage ${stunned ? 'boss-art-stage--stunned' : ''}`}
      aria-hidden="true"
    >
      <img
        className={`boss-uploaded-art boss-art-layer boss-art-layer--idle ${
          activePose ? '' : 'is-active'
        }`}
        src={idleUrl}
        alt=""
      />
      {(Object.entries(actionImages) as [BossAttackKind, string][]).map(
        ([kind, src]) => (
          <img
            key={kind}
            className={`boss-uploaded-art boss-art-layer boss-art-layer--${kind} ${
              activePose === kind ? 'is-active' : ''
            }`}
            src={src}
            alt=""
          />
        ),
      )}
    </div>
  );
}

function BossAttackEffect({
  effect,
  damage,
}: {
  effect: BossAttackFx;
  damage: number;
}) {
  const t = useT();

  return (
    <div
      className={`boss-attack-effect boss-attack-effect--${effect.kind} boss-attack-effect--${effect.result}`}
      aria-hidden="true"
    >
      <i className="boss-impact-flash" />
      <i className="boss-impact-wave" />
      <i className="boss-core-beam" />
      <div className="boss-rock-shards">
        {Array.from({ length: 8 }, (_, index) => (
          <i
            key={index}
            style={
              {
                '--shard-index': index,
                '--shard-x': `${12 + ((index * 13) % 72)}%`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
      <b className="boss-impact-label">
        {effect.result === 'blocked'
          ? t('boss.blocked')
          : effect.result === 'interrupted'
            ? t('boss.interrupted')
            : t('boss.grip_damage', { damage })}
      </b>
    </div>
  );
}

function Meter({
  value,
  color,
  label,
}: {
  value: number;
  color: string;
  label: string;
}) {
  return (
    <div className="boss-meter">
      <div className="boss-meter-label">{label}</div>
      <div className="boss-meter-track">
        <i
          style={{
            width: `${Math.max(0, Math.min(100, value))}%`,
            background: color,
            boxShadow: `0 0 10px ${color}`,
          }}
        />
      </div>
    </div>
  );
}
